import { ConfigCmd, FilterMode, Item, LoaderPoint, LoaderPriority } from "dsabp-js"
import { Image, createCanvas } from "../canvas.js"
import { rendererResolvers } from "../main.js"
import { clearCanvas, drawRotatedImage, getImage, getRotated, getTinted, toImage } from "../util.js"

const baseColor = "#2b9430"

const priorityColors = new Map<LoaderPriority, string>()
	.set(LoaderPriority.HIGH, "#00ff00")
	.set(LoaderPriority.LOW, "#ff0000")

const modeColors = new Map<FilterMode, string>()
	.set(FilterMode.ALLOW_ALL, "#009b00")
	.set(FilterMode.ALLOW_FILTER_ONLY, "#9b6700")
	.set(FilterMode.BLOCK_ALL, "#9b0000")
	.set(FilterMode.BLOCK_FILTER_ONLY, "#9b9b00")

const armColor = "#222222"

const pickupRotations = new Map<LoaderPoint, number>()
	.set(LoaderPoint.BOTTOM, -90)
	.set(LoaderPoint.BOTTOM_LEFT, -45)
	.set(LoaderPoint.BOTTOM_RIGHT, -90 - 45)
	.set(LoaderPoint.LEFT, 0)
	.set(LoaderPoint.RIGHT, 180)
	.set(LoaderPoint.TOP, 90)
	.set(LoaderPoint.TOP_LEFT, 45)
	.set(LoaderPoint.TOP_RIGHT, 90 + 45)

const dropRotations = new Map<LoaderPoint, number>()
	.set(LoaderPoint.BOTTOM, 90)
	.set(LoaderPoint.BOTTOM_LEFT, 90 + 45)
	.set(LoaderPoint.BOTTOM_RIGHT, 45)
	.set(LoaderPoint.LEFT, 180)
	.set(LoaderPoint.RIGHT, 0)
	.set(LoaderPoint.TOP, -90)
	.set(LoaderPoint.TOP_LEFT, -90 - 45)
	.set(LoaderPoint.TOP_RIGHT, -45)

const CACHE_KEY_FLAT_ARMS = "Loader_flatArms",
	CACHE_KEY_DIAGONAL_ARMS = "Loader_diagonalArms"

rendererResolvers.push(
	({ main, toRel, item, config }) => {
		if (item != Item.LOADER_NEW)
			return

		const filterMode = config.filterMode ?? ConfigCmd.defaults.filterMode
		const priority = config.loader?.priority ?? ConfigCmd.defaults.loader.priority
		const pickupPoint = config.loader?.pickupPoint ?? ConfigCmd.defaults.loader.pickupPoint
		const dropPoint = config.loader?.dropPoint ?? ConfigCmd.defaults.loader.dropPoint

		return {
			discriminator: `${item.id}_${filterMode.enumValue}_${priority.enumValue}_${pickupPoint.enumValue}_${dropPoint.enumValue}`,
			async render() {
				const canvas = createCanvas(0, 0)
				const ctx = canvas.getContext("2d")
				const canvas2 = createCanvas(0, 0)
				const ctx2 = canvas2.getContext("2d")

				const priorityColor = priorityColors.get(priority)
				const pickupAngle = pickupRotations.get(pickupPoint)
				const dropAngle = dropRotations.get(dropPoint)

				const imgBase = await getTinted(getImage(item.buildInfo[0].image), baseColor)
				const imgMid = await getImage("loader_flux")
				const imgArrow = await getTinted(getRotated(getImage("loader_arrow"), dropAngle), modeColors.get(filterMode))
				const imgPriority = !priorityColor ? null : await getTinted(getImage("loader_priority"), priorityColor)

				const extraPx = 8 // for diagonal hand rotation

				clearCanvas(canvas, ctx, imgBase.width, imgBase.height)

				clearCanvas(canvas2, ctx2, imgBase.width * 3 + extraPx * 2, imgBase.height * 3 + extraPx * 2)
				ctx2.translate(imgBase.width + extraPx, imgBase.height + extraPx)

				ctx.drawImage(imgBase, 0, 0)
				ctx.drawImage(imgMid, 0, 0)
				ctx.drawImage(imgArrow, 0, 0)

				if (imgPriority) {
					if (priority == LoaderPriority.LOW) {
						ctx.save()
						ctx.scale(1, -1)
						ctx.drawImage(imgPriority, 0, 0 - imgPriority.height)
						ctx.restore()
					} else {
						ctx.drawImage(imgPriority, 0, 0)
					}
				}


				if (pickupAngle % 90 == 0) {
					const arms = await getFlatArmsImage(main.imageCache)
					drawRotatedImage(ctx2, arms, pickupAngle, arms.width * 0.25, arms.height / 2 - 4, arms.width * 0.75, arms.height / 2)
				} else {
					const arms = await getDiagonalArmsImage(main.imageCache)
					drawRotatedImage(ctx2, arms, pickupAngle + 45, arms.width * 0.25, arms.height / 4, arms.width * 0.75 + 8, arms.height / 4 - 8)
				}

				return [
					{
						image: await toImage(canvas)
					},
					{
						layer: "top",
						image: await toImage(canvas2),
						textureSizeOff: toRel(canvas2.width - imgBase.width, canvas2.height - imgBase.height),
						texturePosOff: toRel(-imgBase.width - extraPx, -imgBase.height - extraPx)
					}
				]
			}
		}
	}
)

async function getFlatArmsImage(imageCache: Record<string, Image>) {
	const cached = imageCache[CACHE_KEY_FLAT_ARMS]; if (cached) return cached

	const imgHand = await getImage("loader_hand"),
		canvas = createCanvas(imgHand.width * 2, imgHand.height + 8),
		ctx = canvas.getContext("2d")

	ctx.translate(0, 4)

	ctx.strokeStyle = armColor
	ctx.lineWidth = 4
	ctx.beginPath()
	ctx.moveTo(imgHand.width / 2, 0)
	ctx.lineTo(imgHand.width * 1.5, 8)
	ctx.stroke()
	ctx.beginPath()
	ctx.moveTo(imgHand.width / 2, imgHand.height)
	ctx.lineTo(imgHand.width * 1.5, imgHand.height - 8)
	ctx.stroke()

	ctx.drawImage(imgHand, 0, 0)
	ctx.scale(-1, -1)
	ctx.drawImage(imgHand, -imgHand.width, -imgHand.height)

	return imageCache[CACHE_KEY_FLAT_ARMS] = await toImage(canvas)
}

async function getDiagonalArmsImage(imageCache: Record<string, Image>) {
	const cached = imageCache[CACHE_KEY_DIAGONAL_ARMS]; if (cached) return cached

	const imgHand = await getImage("loader_hand")
	const canvas = createCanvas(imgHand.width * 2, imgHand.height * 2)
	const ctx = canvas.getContext("2d")

	ctx.translate(0, imgHand.height * 1.5 - 8)
	ctx.rotate(-45 * Math.PI / 180)
	ctx.strokeStyle = armColor
	ctx.lineWidth = 4
	ctx.beginPath()
	ctx.moveTo(imgHand.width / 2, 0)
	ctx.lineTo(imgHand.width * 1.5 + 16, 8)
	ctx.stroke()
	ctx.beginPath()
	ctx.moveTo(imgHand.width / 2, imgHand.height)
	ctx.lineTo(imgHand.width * 1.5 + 16, imgHand.height - 8)
	ctx.stroke()

	ctx.drawImage(imgHand, 0, 0)
	ctx.scale(-1, -1)
	ctx.drawImage(imgHand, -imgHand.width, -imgHand.height)

	return imageCache[CACHE_KEY_DIAGONAL_ARMS] = await toImage(canvas)
}
